import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Mail, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface WhatsAppButtonProps {
  phoneNumber: string;
  email: string;
  location: string;
}

const WhatsAppButton = ({ phoneNumber, email, location }: WhatsAppButtonProps) => {
  const [isInfoVisible, setIsInfoVisible] = useState(false);
  const { t } = useTranslation();

  return (
    <div className="fixed bottom-8 right-8 z-50">
      <AnimatePresence>
        {isInfoVisible && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="absolute bottom-full right-0 mb-4 bg-white rounded-lg shadow-lg p-4 min-w-[200px]"
          >
            <div className="space-y-3">
              <a 
                href={`https://wa.me/${phoneNumber.replace(/\+/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 text-[#41392e] hover:text-[#c9ab81] transition-colors duration-300"
                aria-label={t('whatsapp.chat')}
              >
                <MessageCircle className="w-4 h-4" />
                <span>{phoneNumber}</span>
              </a>
              <a 
                href={`mailto:${email}`}
                className="flex items-center space-x-2 text-[#41392e] hover:text-[#c9ab81] transition-colors duration-300"
                aria-label={t('whatsapp.email')}
              >
                <Mail className="w-4 h-4" />
                <span>{email}</span>
              </a>
              <div className="flex items-center space-x-2 text-[#41392e]">
                <MapPin className="w-4 h-4" />
                <span>{location}</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        className="bg-[#c9ab81] text-white p-4 rounded-full shadow-lg hover:bg-[#41392e] transition-colors duration-300"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setIsInfoVisible(!isInfoVisible)}
        aria-label={t('whatsapp.toggle')}
      >
        <MessageCircle className="w-6 h-6" />
      </motion.button>
    </div>
  );
};

export default WhatsAppButton;